import type { QsoType, LogbookType, LookupServiceResponseType } from "@/types";

import { defineStore } from "pinia";
import { watch, shallowReactive } from "vue";
import { watchDebounced } from '@vueuse/core'
import { useCallsignLookupsStore } from "@/stores/CallsignLookupStore";

import LogbookService from "@/services/LogbookService";
import PotaParkService from "@/services/PotaParkService";
import QsoService from "@/services/QsoService";
import { mapFlrigModeToCommonMode } from "@/services/FlrigService";
import QsoFactory from "@/factories/QsoFactory";
import LookupServiceResponseToQsoTransformer from "@/transformers/LookupServiceResponseToQsoTransformer";
import { filterLookupDataForQso } from "@/utils/qsoUpdateHelpers";

import { useProfileStore } from "./ProfileStore";
import { useRigControlStore } from "@/stores/RigControlStore";
import { Logger } from "@/models/Logger";
import { storeToRefs } from "pinia";
import { formatMhz } from "@/utils/formatters";
import { startCallsignStateMachine } from '@/machines/callsignMachine';

// Pending sticky field persist promise (not reactive, not part of Pinia state)
let _pendingStickyPersist: Promise<void> | null = null;

// Explicit state type for Pinia store (local, not exported)
type LoggerState = {
  callsignStateMachine: any,
  currentLogbook: LogbookType | null,
  currentQso: QsoType,
  logger: Logger | null,
  expandedMode: boolean,
  timeIsLive: boolean,
  timer: any,
  qsoList: QsoType[],
  stickyFields: Partial<QsoType>,
  watchersInitialized: boolean,
  watcherStoppers: (() => void)[],
  _duplicateQsoIds: string[],
};

export const useLoggerStore = defineStore("logger", {
  state: (): LoggerState => ({
    callsignStateMachine: null,
    currentLogbook: null as LogbookType | null,
    currentQso: {} as QsoType,
    logger: null as Logger | null,
    expandedMode: false,
    timeIsLive: true,
    timer: null as any,
    qsoList: shallowReactive([]) as QsoType[],
    stickyFields: {} as Partial<QsoType>,
    watchersInitialized: false,
    watcherStoppers: [] as (() => void)[],
    _duplicateQsoIds: [] as string[],
  }),

  getters: {
    lastQsoByCreatedAt: (state): QsoType | null => {
      if (state.qsoList.length === 0) return null;
      // Find the QSO with the latest `createdAt`
      return state.qsoList.reduce((latest, qso) => {
        return new Date(qso.createdAt!) > new Date(latest.createdAt!) ? qso : latest;
      }, state.qsoList[0]);
    },
    duplicateQsoIds: (state): string[] => {
      // Return cached duplicate IDs updated by debounced watcher
      return state._duplicateQsoIds;
    },

    lastQsoByDateTime: (state): QsoType | null => {
      if (state.qsoList.length === 0) return null;
      // Parse and compare `qsoDate` and `timeOn`
      return state.qsoList.reduce((latest, qso) => {
        return new Date(qso.qsoDateTime!) > new Date(latest.qsoDateTime!) ? qso : latest;
      }, state.qsoList[0]);
    },
  },

  actions: {
    clearLastLookup() {
      const callsignLookupsStore = useCallsignLookupsStore();
      callsignLookupsStore.clearLastLookup();
    },
    setToInitialValues() {
      this.currentLogbook = null;
      this.currentQso = {} as QsoType;
      this.expandedMode = false;
      this.logger = null;
      this.timeIsLive = true;
      this.timer = null;
      this.qsoList = [];
      this.stickyFields = {};
      this.watchersInitialized = false;
      this.watcherStoppers = [];
    },

    // Initialization and Reset
    async initLogger(logbookId: string) {
      try {
        this.stickyFields = {};
        this.startLiveTime();
        this.clearLastLookup();
        await this.fetchCurrentLogbook(logbookId);

        await this.initializeLoggerTemplate();
        // Fetch QSO list first since other operations depend on it
        await this.fetchQsoList(logbookId);
        // Setup sticky fields first (may JIT-migrate to logbook), then init QSO with those values
        await this.setupStickyFields();
        await this.initQso();

        this.initWatchers();
      } catch (error) {
        console.error('Error initializing logger:', error);
      }
    },

    // Helper: derive propagation mode from satellite name
    derivePropModeFromSatName(satName?: string | null): '' | 'SAT' {
      const trimmed = (satName ?? '').toString().trim();
      return trimmed === '' ? '' : 'SAT';
    },

    initWatchers() {
      // Clear existing watchers
      this.clearWatchers();

      // (Re)start the callsign-state-machine service
      if (this.callsignStateMachine) this.callsignStateMachine.stop();   // dispose old
      this.callsignStateMachine = startCallsignStateMachine();

      const rigControlStore = useRigControlStore();
      const { frequency, mode, power, rigControlAvailable } = storeToRefs(rigControlStore);

      // Watch frequency
      const stopFrequencyWatcher = watch(
        frequency,
        (newFrequency) => {
          if (newFrequency && rigControlAvailable.value) {
            const frequencyMHz = (newFrequency / 1_000_000);
            this.updateCurrentQso({ freq: formatMhz(frequencyMHz) });
          }
        },
        { immediate: true }
      );
      // Watch mode
      const stopModeWatcher = watch(
        mode,
        (newMode) => {
          if (newMode && rigControlAvailable.value) {

            const normalizedMode = mapFlrigModeToCommonMode(newMode);
            this.updateCurrentQso({ mode: normalizedMode });
            this.updateRstValues();
          }
        },
        { immediate: true }
      );

      // Watch power
      const stopPowerWatcher = watch(
        power,
        (newPower) => {
          if (newPower !== null && newPower !== undefined && rigControlAvailable.value && rigControlStore.powerPollingEnabled) {
            this.updateCurrentQso({ txPwr: newPower });
          }
        },
        { immediate: true }
      );

      // Watch currentQso.mode for RST updates
      const stopQsoModeWatcher = watch(
        () => this.currentQso.mode,
        (newMode) => {
          if (newMode) {
            this.updateRstValues();
          }
        },
        { immediate: true }
      );
      const stopCallsignWatcher = watchDebounced(
        () => this.currentQso.call,
        (newCallsign) => {
          // Always send event to XState machine for all callsign changes
          this.callsignStateMachine.send({ type: 'ENTER_CALLSIGN', callsign: newCallsign });
        },
        {
          debounce: 250,
          immediate: false
        }
      );
      const stopMyParkWatcher = watchDebounced(
        () => this.currentQso.myPotaRef,
        (newRef, oldRef) => {
          // Skip if this is the initial value being set
          if (!oldRef && newRef) {
            return;
          }

          // Only proceed if we have a new ref or we're explicitly clearing it
          if (newRef || newRef === '') {
            this.updateCurrentQsoFromMyParkRef(newRef);
          }
        },
        {
          debounce: 1000,
          immediate: false // Don't fire immediately on setup
        }
      );

      // Watch satellite name → set propagation mode only when value change requires it
      const stopSatNameWatcher = watch(
        () => this.currentQso.satName,
        (newSatName) => {
          const desired = this.derivePropModeFromSatName(newSatName);
          if (this.currentQso.propMode !== desired) {
            this.updateCurrentQso({ propMode: desired });
          }
        },
        { immediate: true }
      );

      // Debounced duplicate detection to avoid expensive recalculations on every save
      const stopDuplicateWatcher = watchDebounced(
        () => this.qsoList,
        (newList) => {
          if (this.logger) {
            this._duplicateQsoIds = this.logger.duplicateQsoIds(newList);
          }
        },
        { debounce: 500, immediate: true }
      );

      // Sync sticky-field subset of currentQso into stickyFields and persist to logbook
      const stopStickyFieldsSyncWatcher = watchDebounced(
        () => {
          if (!this.logger?.stickyFields) return null;
          const snapshot: Record<string, any> = {};
          for (const field of this.logger.stickyFields) {
            const key = field as any as keyof QsoType;
            snapshot[key] = this.currentQso[key];
          }
          return snapshot;
        },
        (newSnapshot) => {
          if (newSnapshot && this.currentLogbook) {
            Object.assign(this.stickyFields, newSnapshot);
            this.persistStickyFieldsToLogbook();
          }
        },
        { debounce: 2000, deep: true, immediate: false }
      );

      // Store stopper functions
      this.watcherStoppers.push(
        stopFrequencyWatcher,
        stopModeWatcher,
        stopPowerWatcher,
        stopQsoModeWatcher,
        stopCallsignWatcher,
        stopMyParkWatcher,
        stopSatNameWatcher,
        stopDuplicateWatcher,
        stopStickyFieldsSyncWatcher
      );
    },

    clearWatchers() {
      this.watcherStoppers.forEach((stop) => stop());
      this.watcherStoppers = [];
    },

    clearForm() {
      this.clearLastLookup();
      if (this.currentQso && this.logger?.stickyFields) {
        const activeProfile = useProfileStore().activeProfile;
        const operatorCallsign = activeProfile?.operator.callsign || "";

        // Save the values of sticky fields
        const stickyValues = this.logger.stickyFields.reduce((acc, key) => {
          // @ts-ignore
          acc[key] = this.currentQso[key];
          return acc;
        }, {} as Partial<QsoType>);
        // Create new QSO with defaults and restore sticky values
        this.currentQso = {
          ...QsoFactory.newQso(
            this.currentLogbook!,
            stickyValues,
            operatorCallsign
          )
        }
      }
    },

    async setupStickyFields() {
      if (!this.currentLogbook) return;

      if (this.lastQsoByDateTime) {
        // Primary source: infer from last QSO (backward compatible)
        this.setStickyFields(
          this.currentLogbook.template,
          this.lastQsoByDateTime
        );
        // JIT migration: persist inferred values to logbook if not yet stored
        if (!this.currentLogbook.stickyFieldValues) {
          await this.persistStickyFieldsToLogbook();
        }
      } else if (this.currentLogbook.stickyFieldValues) {
        // Fallback: restore from logbook record (no QSOs exist)
        this.stickyFields = { ...this.currentLogbook.stickyFieldValues };
      }
    },
    async initializeLoggerTemplate() {
      if (!this.currentLogbook) return;
      this.logger = await Logger.init(this.currentLogbook)
    },

    async resetLogger() {
      if (this.currentLogbook) {
        await this.initQso();
      }
    },

    // Logbook Actions
    async fetchCurrentLogbook(logbookId: string) {
      // Drain any pending sticky field persist to avoid stale reads
      if (_pendingStickyPersist) {
        await _pendingStickyPersist;
        _pendingStickyPersist = null;
      }
      try {
        this.currentLogbook = await LogbookService.findOne(logbookId);
      } catch (error) {
        // A missing logbook (deleted, or removed by sync before this navigation)
        // is expected: leave currentLogbook null so the page can redirect, and
        // don't let the PouchDB 404 escape as an unhandled rejection. Re-throw
        // anything else.
        if ((error as { status?: number })?.status === 404) {
          this.currentLogbook = null;
          return;
        }
        throw error;
      }
    },

    // QSO Actions
    async fetchQsoList(logbookId: string) {
      const qsos = await QsoService.findAllByLogbook(logbookId);
      this.qsoList = qsos || []
    },

    async setOperator(operator: string) {
      this.$patch({
        currentQso: {
          ...this.currentQso,
          operator: operator,
        },
      });
    },

    async initQso() {
      this.clearLastLookup();
      const activeProfile = useProfileStore().activeProfile;
      if (!this.currentLogbook) return;

      const previousQso = this.timeIsLive ? this.lastQsoByDateTime : this.lastQsoByCreatedAt;
      // Create a new QSO with initial values
      const newQso = QsoFactory.newQso(
        this.currentLogbook,
        this.stickyFields,
        activeProfile?.operator.callsign || ""
      );
      // Normalize propagation mode from satellite name at init
      const derivedPropMode = this.derivePropModeFromSatName(newQso.satName);
      if (newQso.propMode !== derivedPropMode) {
        newQso.propMode = derivedPropMode;
      }

      if (!this.timeIsLive && previousQso) {
        // When time is paused, carry over timeOn and qsoDate from the last QSO
        newQso.timeOn = previousQso.timeOn;
        newQso.qsoDate = previousQso.qsoDate;
      } else if (this.timeIsLive) {
        newQso.timeOn = new Date().toISOString().substring(11, 19); // Time in HH:MM:SS
        newQso.qsoDate = new Date().toISOString().substring(0, 10); // Date in YYYY-MM-DD
      }
      // Initialize from RigControlStore
      const rigControlStore = useRigControlStore();
      if (rigControlStore.rigControlAvailable) {
        const frequency = rigControlStore.frequency / 1_000_000
        //@ts-ignore
        newQso.freq = formatMhz(frequency)
        newQso.mode = rigControlStore.mode;
        // Skip applying polled power when power polling is disabled so the
        // sticky/user-set power value is carried over instead of overwritten.
        if (rigControlStore.powerPollingEnabled) {
          newQso.txPwr = rigControlStore.power;
        }
      }
      this.currentQso = newQso;
    },

    async updateCurrentQso(attrs: Record<string, string | number | boolean>) {
      if (this.currentQso) {
        this.$patch({
          currentQso: {
            ...this.currentQso,
            ...attrs,
          },
        });
      }
    },
    updateCurrentQsoFromLookupResponse(response: LookupServiceResponseType) {
      if (this.currentQso) {
        const transformedResponse =
          LookupServiceResponseToQsoTransformer(response);
        const filteredResponse = filterLookupDataForQso(
          this.currentQso,
          transformedResponse
        );

        this.$patch({
          currentQso: {
            ...this.currentQso,
            ...filteredResponse,
          },
        });
      }
    },
    clearCurrentQsoList() {
      this.qsoList = [];
    },
    clearCurrentLogbook() {
      this.currentLogbook = null;
    },
    /**
     * React to a logbook being deleted. If it is the one currently loaded in the
     * logger, tear the logger down WITHOUT persisting sticky fields -- otherwise
     * the debounced sticky-field watcher (or resetStore on unmount) would fire
     * `updateStickyFieldValues` against a now-deleted id and resurrect a corrupt
     * shell logbook (no title/createdAt/template) that then syncs everywhere.
     * No-op when a different logbook is active.
     */
    handleLogbookDeleted(deletedLogbookId: string) {
      if (this.currentLogbook?._id !== deletedLogbookId) return;
      // Drop any in-flight persist reference so fetchCurrentLogbook won't await
      // (or re-trigger) a write for the deleted logbook.
      _pendingStickyPersist = null;
      // Full teardown. Leaving `logger` set while `currentLogbook` is null lets
      // callers like clearForm()/initQso() dereference a null logbook. Stop the
      // watchers and the callsign machine, clear the timer, then reset all
      // logger state so persistStickyFieldsToLogbook()/resetStore() and the form
      // helpers short-circuit on their guards.
      this.clearWatchers();
      this.clearTimer();
      if (this.callsignStateMachine) {
        this.callsignStateMachine.stop();
        this.callsignStateMachine = null;
      }
      this.setToInitialValues();
    },
    clearCurrentQso() {
      this.currentQso = {} as QsoType;
    },
    resetStore() {
      // Compute sticky field values directly from currentQso (bypasses debounce chain)
      const logbookId = this.currentLogbook?._id;
      const fieldsSnapshot: Partial<QsoType> = {};
      if (this.logger?.stickyFields && this.currentQso) {
        for (const field of this.logger.stickyFields) {
          const key = field as any as keyof QsoType;
          if (this.currentQso[key] !== undefined && this.currentQso[key] !== '') {
            fieldsSnapshot[key] = this.currentQso[key] as any;
          }
        }
      }

      this.clearCurrentQsoList();
      this.clearCurrentLogbook();
      this.clearCurrentQso();
      this.clearTimer();
      this.clearWatchers();
      this.setToInitialValues();

      // Persist with captured values; track promise so fetchCurrentLogbook can drain it
      if (logbookId && Object.keys(fieldsSnapshot).length > 0) {
        _pendingStickyPersist = LogbookService.updateStickyFieldValues(logbookId, fieldsSnapshot)
          .catch(err => console.error('Failed to persist sticky fields on reset:', err));
      }
    },
    clearCurrentQsoLookupFields() {
      if (this.currentQso) {
        // Destructure currentQso and set defaults for the properties you want to clear
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          lookupSuccess,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          lookupService,
          ...restOfQso
        } = this.currentQso || {};

        this.$patch({
          currentQso: {
            ...restOfQso,
            lookedUp: false,
            name: '',
            gridsquare: '',
            state: '',
            qth: '',
            country: '',
            cnty: '',
          },
        });
      }
    },
    async saveCurrentQso() {
      if (this.currentQso && this.currentLogbook) {
        const potaRef = this.currentQso.potaRef;
        const callsign = this.currentQso.call;
        // Set sticky fields for future QSOs
        this.setStickyFields(this.currentLogbook.template, this.currentQso);

        if ((potaRef && potaRef.includes(',')) || (callsign && callsign.includes(','))) {
          const savedQsos = await QsoService.batchCreate({
            attrs: this.currentQso,
            logbook: this.currentLogbook,
          });
          // Tanstack won't react if we don't do this 1 weird trick
          this.qsoList = [...this.qsoList, ...savedQsos];
        } else {
          const savedQso = await QsoService.create({
            attrs: this.currentQso,
            logbook: this.currentLogbook,
          });
          if (savedQso) {
            this.qsoList = [...this.qsoList, savedQso];
          }
        }
      }
    },
    setStickyFields(formType: string, qso: QsoType) {
      if (this.logger && this.logger.stickyFields) {
        this.logger.stickyFields.forEach((field: any) => {
          const key = field as keyof QsoType;
          if (key in qso) {
            this.stickyFields[key] = qso[key] as any;
          }
        });
      }
    },

    async persistStickyFieldsToLogbook() {
      if (!this.currentLogbook?._id || Object.keys(this.stickyFields).length === 0) return;
      try {
        await LogbookService.updateStickyFieldValues(
          this.currentLogbook._id,
          { ...this.stickyFields }
        );
        // Re-fetch to keep _rev in sync and avoid PouchDB conflicts on subsequent updates
        this.currentLogbook = await LogbookService.findOne(this.currentLogbook._id);
      } catch (error) {
        console.error('Failed to persist sticky fields to logbook:', error);
      }
    },

    async deleteQso(qsoId: string) {
      await QsoService.delete(qsoId);
      this.qsoList = this.qsoList.filter((qso) => qso._id !== qsoId);
    },

    // Time Management Actions
    updateCurrentQsoTimeOn() {
      const currentTime = new Date();
      const newTimeOn = currentTime.toISOString().substring(11, 19); // Time in HH:MM:SS
      const newQsoDate = currentTime.toISOString().substring(0, 10); // Date in YYYY-MM-DD

      this.$patch((state) => {
        // Update timeOn every second
        state.currentQso.timeOn = newTimeOn;

        // Only update qsoDate if it has changed (i.e., when a new day starts)
        if (state.currentQso.qsoDate !== newQsoDate) {
          state.currentQso.qsoDate = newQsoDate;
        }
      });
    },

    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    startLiveTime() {
      if (this.timer) return; // If timer is already running, avoid setting a new one
      this.clearTimer();       // Just in case, clear the previous timer
      this.timeIsLive = true;
      this.updateCurrentQsoTimeOn();
      this.timer = setInterval(() => {
        this.updateCurrentQsoTimeOn();
      }, 1000);
    },

    stopLiveTime() {
      this.clearTimer();
      this.timeIsLive = false;
    },

    toggleLiveTime() {
      this.timeIsLive ? this.stopLiveTime() : this.startLiveTime();
    },
    updateRstValues() {
      if (!this.currentQso) return;
      const rstModes = ['CW'];
      if (rstModes.includes(this.currentQso.mode!)) {
        if (this.currentQso.rstRcvd === '59') {
          this.currentQso.rstRcvd = '599';
        }
        if (this.currentQso.rstSent === '59') {
          this.currentQso.rstSent = '599';
        }
      } else {
        if (this.currentQso.rstRcvd === '599') {
          this.currentQso.rstRcvd = '59';
        }
        if (this.currentQso.rstSent === '599') {
          this.currentQso.rstSent = '59';
        }
      }
    },

    async updateCurrentQsoFromMyParkRef(myPotaRef: string) {
      if (myPotaRef == '') {
        this.updateCurrentQso({
          myGridsquare: '',
          myState: '',
          myCnty: ''
        })
        return
      }
      try {
        const park = await PotaParkService.fetchById(myPotaRef);
        const locations = park.location.split(',');
        let location = ''
        if (locations.length == 1) {
          location = locations[0].split('-')[1];
        }
        this.updateCurrentQso({
          myGridsquare: park.grid,
          myState: location,
          myCnty: ''
        })
      }
      catch (error) {
        this.updateCurrentQso({
          myGridsquare: '',
          myState: '',
          myCnty: ''
        })
      }
    },
  },
});
